import { differenceInBusinessDays, isAfter, isBefore, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { OfferInventoryContext } from '~/types/inventoryWizard';

interface ListSchedule {
  start: string;
  end: string;
}

interface DetailedOfferExtras {
  deal_tier?: string | null;
  packages?: Array<{ allow_buy_now_book_later?: boolean }>;
}

function deriveScheduleContext(
  listSchedule: ListSchedule | undefined,
): Pick<OfferInventoryContext, 'recent_go_live' | 'campaign_live'> {
  if (!listSchedule?.start || !listSchedule?.end) {
    return { recent_go_live: null, campaign_live: null };
  }
  const now = new Date();
  const start = parseISO(listSchedule.start);
  const end = parseISO(listSchedule.end);
  return {
    campaign_live: !isBefore(now, start) && !isAfter(now, end),
    recent_go_live: !isBefore(now, start) && differenceInBusinessDays(now, start) <= 3,
  };
}

function parseTier1Or2(dealTier: string | null | undefined): boolean | null {
  if (!dealTier) return null;
  const num = parseInt(dealTier.match(/\d+$/)?.[0] ?? '', 10);
  if (isNaN(num)) return null;
  return num <= 2;
}

function deriveHasBnbls(packages: Array<{ allow_buy_now_book_later?: boolean }> | undefined): boolean | null {
  if (!packages?.length) return null;
  const known = packages.filter((p) => p.allow_buy_now_book_later !== undefined);
  if (!known.length) return null;
  return known.some((p) => p.allow_buy_now_book_later === true);
}

export function useOfferInventoryContext(
  offerId: string | null,
): OfferInventoryContext & { isLoading: boolean; isError: boolean } {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { data, isLoading, isError } = useGetOffer(offerId ?? undefined);

  if (!data)
    return { recent_go_live: null, campaign_live: null, tier_1_or_2: null, has_bnbls: null, isLoading, isError };

  const extras = data as typeof data & DetailedOfferExtras;
  // list_visibility is the correct schedule type for campaign_live: svc-offer enforces that
  // list_visibility windows are always contained within both availability and online_purchase
  // windows, so an active list_visibility schedule implies the offer is bookable.
  const listSchedule = data.brand_schedules?.find((s) => s.type === 'list_visibility' && s.brand === brand);

  return {
    ...deriveScheduleContext(listSchedule),
    tier_1_or_2: parseTier1Or2(extras.deal_tier),
    has_bnbls: deriveHasBnbls(extras.packages),
    isLoading,
    isError,
  };
}
